import { useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Fragment } from "react/jsx-runtime";
import { Button } from "../../../../../components/buttons/button";
import { Icon } from "../../../../../components/icons/icon";
import { TitleSubLoader } from "../../../../../components/loaders/titleSubLoader";
import { ManageApprovalChainModal } from "../../../../../components/modals/app_modals/manageApprovalChainModal";
import { TitleSub } from "../../../../../components/typography/titleSub";
import { useModalControl } from "../../../../../customHooks/useModalControl";
import { deepCopy } from "../../../../../helperFunctions";
import { useFetchCooperativeApprovalChainsQuery } from "../../../../../redux/features/approvals/approvals.api";
import {
  CooperativeAction,
  SingleApprovalChain,
} from "../../../../../redux/features/approvals/approvals.type";
import {
  approvalChainsMap,
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../../../../utils/constants";

export const ApprovalsSettings = () => {
  const [selectedAction, setSelectedAction] = useState<CooperativeAction>();
  const [selectedApprovalChain, setSelectedApprovalChain] =
    useState<SingleApprovalChain[]>();

  const { handleModalClose, handleModalOpen, modal, modalOpen } =
    useModalControl();

  const {
    data: approvalChains,
    isFetching: fetchingApprovalChains,
    refetch: refetchApprovalChains,
  } = useFetchCooperativeApprovalChainsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  return (
    <>
      <div className="flex flex-col gap-3">
        {fetchingApprovalChains
          ? Object.keys(approvalChainsMap).map((k) => {
              return (
                <div
                  key={`${k}`}
                  className="p-4 flex flex-col gap-4 bg-light-green-bg border border-border-grey-3 rounded-lg"
                >
                  <TitleSubLoader />

                  <div className="flex items-center gap-2 flex-wrap">
                    <Skeleton
                      width={"200px"}
                      height={"20px"}
                      baseColor={SKELETON_BASE_COLOR}
                      highlightColor={SKELETON_HIGHLIGHT_COLOR}
                    />

                    <p className="text-pry-dark-green-300/50">|</p>

                    <Skeleton
                      width={"115px"}
                      height={"20px"}
                      baseColor={SKELETON_BASE_COLOR}
                      highlightColor={SKELETON_HIGHLIGHT_COLOR}
                    />
                  </div>
                </div>
              );
            })
          : undefined}

        {!fetchingApprovalChains && !!approvalChains?.data ? (
          <>
            {Object.keys(approvalChainsMap).map((key) => {
              const chainKey = key as CooperativeAction;

              const approvalChain = approvalChainsMap[chainKey];

              const approvalChainConfiguration = deepCopy(
                approvalChains?.data[chainKey] ?? []
              );

              const approvalLevels = approvalChainConfiguration?.sort(
                (a, b) => a.CodeLevel - b.CodeLevel
              );

              if (
                chainKey === "changeOfficialApproval" ||
                chainKey === "settingApproval"
              ) {
                return undefined;
              }

              return (
                <div
                  key={`${chainKey}`}
                  className="p-4 flex flex-col gap-4 bg-light-green-bg border border-border-grey-3 rounded-lg"
                >
                  <TitleSub
                    title={approvalChain.title}
                    subtitle={approvalChain.subtext}
                    titleClassName="!text-pry-green-200"
                    subtitleClassName="!text-pry-dark-green-300/60"
                    className=""
                  />

                  <div className="flex flex-wrap gap-2 items-center text-sm">
                    <p className="flex flex-wrap items-center gap-1 font-medium">
                      {approvalLevels.map((level) => {
                        return (
                          <Fragment key={`${level.id}`}>
                            <span className="min-w-max">{level.Role.Name}</span>
                            <Icon
                              svg="left-arrow"
                              width={14}
                              height={14}
                              className="last:hidden rotate-180"
                            />
                          </Fragment>
                        );
                      })}
                    </p>

                    <p className="text-pry-dark-green-300/50">|</p>

                    <Button
                      type="button"
                      className="!p-0 underline !w-fit"
                      text="Edit Configuration"
                      variation="plain"
                      onClick={() => {
                        setSelectedAction(chainKey);
                        setSelectedApprovalChain(approvalChainConfiguration);
                        handleModalOpen("editConfigurationModal");
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </>
        ) : undefined}
      </div>

      {!!selectedAction && !!selectedApprovalChain ? (
        <ManageApprovalChainModal
          isModalOpen={modalOpen && modal === "editConfigurationModal"}
          onModalClose={() => {
            setSelectedAction(undefined);
            setSelectedApprovalChain(undefined);
            handleModalClose();
          }}
          successCallback={() => {
            refetchApprovalChains();
            setSelectedAction(undefined);
            setSelectedApprovalChain(undefined);
            handleModalClose();
          }}
          approvalChain={selectedApprovalChain}
          action={selectedAction}
        />
      ) : undefined}
    </>
  );
};
